import React from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap'
import PropTypes from 'prop-types'

import { withTranslation } from 'react-i18next'

import AlertMessage from '../../components/AlertMessage'
import GitOptions from '../../components/GitOptions'
import PageLoader from '../../components/PageLoader'
import projectsActions from './actions'

class ProjectForceImportConfirmModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      // Force to import all files, even if their MD5 sum has not changed.
      allFiles: false,
      commitSha: null,
      gitOptionsIsOpen: false,
      selectedBranch: null,
      selectedBranchName: null,
      updateProjectBranch: false
    }
  }

  componentDidUpdate(prevProps) {
    const {
      branchesFetchingStatus: prevBranchesFetchingStatus,
      fetchingProjectsStatus: prevFetchingProjectsStatus,
      forceImportStatus: prevForceImportStatus,
      repositoryId: prevRepositoryId
    } = prevProps

    const {
      branchesFetchingStatus,
      fetchingProjectsStatus,
      forceImportStatus,
      onClose,
      repositoryId
    } = this.props

    if (prevRepositoryId !== repositoryId) {
      this.resetGitOptions()
    }

    if ((prevBranchesFetchingStatus === 'fetching'
         && branchesFetchingStatus === 'succeeded')
     || (prevFetchingProjectsStatus === 'fetching'
        && fetchingProjectsStatus === 'succeeded')
    ) {
      this.preSelectProjectBranch()
    }

    if (prevForceImportStatus === 'importing'
        && forceImportStatus === 'succeeded') {
      onClose(true) // Pass true to indicate import was triggered
    }
  }

  handleSubmit(event) {
    const { projectId, dispatch } = this.props
    const {
      allFiles,
      commitSha,
      selectedBranch,
      updateProjectBranch
    } = this.state

    event.preventDefault()

    dispatch(projectsActions.forceImport(projectId, allFiles, {
      commitSha,
      selectedBranch: selectedBranch?.label,
      updateProjectBranch
    }))
  }

  preSelectProjectBranch() {
    const { project, projectId, projects } = this.props

    // Use project prop if available, otherwise search in projects array
    const targetProject = project || projects?.find((item) => item.id === projectId)

    if (!targetProject) return

    this.setState({
      selectedBranchName: targetProject.repository_branch_name
    })
  }

  resetGitOptions() {
    this.setState({
      commitSha: null,
      selectedBranch: null,
      updateProjectBranch: false
    })
  }

  toggleGitOptionsIsOpen() {
    const { gitOptionsIsOpen } = this.state

    this.setState({
      gitOptionsIsOpen: !gitOptionsIsOpen
    })
  }

  updateAllFiles(checked) {
    this.setState({
      allFiles: checked
    })
  }

  updateCommitSha(commitSha) {
    this.setState({
      commitSha
    })
  }

  updateSelectedGitBranch(selectedBranch) {
    this.setState({
      selectedBranch,
      updateProjectBranch: false
    })
  }

  updateUpdateProjectBranch(checked) {
    this.setState({
      updateProjectBranch: checked
    })
  }

  render() {
    const {
      forceImportError,
      forceImportStatus,
      isOpen,
      onClose,
      projectId,
      repositoryId,
      t
    } = this.props

    const {
      commitSha,
      gitOptionsIsOpen,
      selectedBranch,
      selectedBranchName,
      updateProjectBranch
    } = this.state

    const branchChanged = selectedBranch
      && selectedBranch.label !== selectedBranchName

    const i18nBase = 'ProjectForceImportConfirmModal'

    return (
      <Modal isOpen={isOpen} toggle={() => onClose()}>
        <Form>
          <ModalHeader toggle={() => onClose()}>
            {t(`${i18nBase}.title`)}
          </ModalHeader>
          <ModalBody>
            <div className="d-flex flex-column">
              <p className="lead">{t(`${i18nBase}.headerMessage`)}</p>
              <ul>
                <li>{t(`${i18nBase}.bodyMessage.newKeys`)}</li>
                <li>{t(`${i18nBase}.bodyMessage.deletedKeys`)}</li>
                <li>{t(`${i18nBase}.bodyMessage.updatedKeys`)}</li>
              </ul>
            </div>

            {(forceImportStatus === 'importing') && (
              <PageLoader message={t(`${i18nBase}.importing`)} />
            )}

            {forceImportStatus === 'failed' && (
              <AlertMessage>{forceImportError}</AlertMessage>
            )}

            <FormGroup check className="mb-4">
              <Label check>
                <Input
                  onChange={
                    ({ target: { checked } }) => this.updateAllFiles(checked)
                  }
                  type="checkbox"
                />
                {t(`${i18nBase}.allFiles`)}
              </Label>
            </FormGroup>

            <GitOptions
              branchChanged={branchChanged}
              commitSha={commitSha}
              isOpen={gitOptionsIsOpen}
              onCommitShaChange={(value) => this.updateCommitSha(value)}
              onBranchChange={
                (selected) => this.updateSelectedGitBranch(selected)
              }
              onUpdateProjectBranchChange={
                (checked) => this.updateUpdateProjectBranch(checked)
              }
              repositoryId={repositoryId}
              selectedBranch={selectedBranch}
              selectedBranchName={selectedBranchName}
              toggle={() => this.toggleGitOptionsIsOpen()}
              updateProjectBranch={updateProjectBranch}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => onClose()}>
              {t('buttons.cancel', { ns: 'commons' })}
            </Button>
            <Button
              color="primary"
              disabled={projectId === null}
              onClick={(event) => this.handleSubmit(event)}
              type="submit"
            >
              {t('buttons.continue', { ns: 'commons' })}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    )
  }
}

ProjectForceImportConfirmModal.propTypes = {
  branchesFetchingStatus: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  fetchingProjectsStatus: PropTypes.string,
  forceImportError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  forceImportStatus: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
    repository_branch_name: PropTypes.string
  }),
  projectId: PropTypes.string,
  projects: PropTypes.arrayOf(PropTypes.shape()),
  repositoryId: PropTypes.number,
  t: PropTypes.func.isRequired
}

ProjectForceImportConfirmModal.defaultProps = {
  branchesFetchingStatus: 'idle',
  fetchingProjectsStatus: 'idle',
  forceImportError: null,
  forceImportStatus: 'idle',
  project: null,
  projectId: null,
  projects: null,
  repositoryId: null
}

const mapStateToProps = ({
  integrations: {
    branchesFetchingStatus
  },
  projects: {
    fetchingAllStatus: fetchingProjectsStatus,
    forceImportError,
    forceImportStatus,
    items: projects
  }
}) => ({
  branchesFetchingStatus,
  fetchingProjectsStatus,
  forceImportError,
  forceImportStatus,
  projects
})

export default withTranslation(['ProjectsPage', 'commons'])(
  connect(mapStateToProps)(ProjectForceImportConfirmModal)
)
